import React, {FC, Fragment, ReactNode, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {cartUpdateQuantities} from '../../store/cart'
import {ICartItemType, IState} from "../../types/state";
import useDebounce from "../../hooks/useDebounce";
import cls from "./shop.module.scss"
import PageHeader from "../shared/PageHeader";
import {TranslationsType} from "../../types/homePageTypes";
import {ICurrencies} from "../ForCWV/Wrappers/MainLayout";
import TableHead from "./CartPieces/TableHead";
import CartActions from "./CartPieces/CartActions";
import MobileCartItems from "./CartPieces/MobileCartItems";
import DesktopCartItems from "./CartPieces/DesktopCartItems";
import CartIsEmpty from "./CartPieces/CartIsEmpty";
import { schemPageCart } from "../schemaorg"
import Script from "next/script";
import {useRouter} from "next/router";
import {useGlobal} from "../../hooks/useGlobal";
import {useToast} from "../../atoms/Toast/useToast";
import {FailSvg} from "../../svg";

interface IProps {
    breadcrumb: Array<{ title: string; url: string }>;
    translationsData: TranslationsType;
    currencies: Array<ICurrencies>;
    locale: string;
    backOrderValue: string,
    dbName: string
}

export type quantitiesType = {
    itemId: number;
    value: number;
    min_qty?: number
}

const ShopPageCart: FC<IProps> = ({breadcrumb, translationsData, locale, backOrderValue, dbName}) => {
    const router = useRouter();
    const cart = useSelector((state: IState) => state.cart);
    const [quantities, setQuantities] = useState<quantitiesType[]>(
        cart?.items.map(elem => ({value: elem.quantity, itemId: elem.id, min_qty: +elem.min_qty || 1}))
    )
    const {selectedRate} = useGlobal()
    const [tbodyContent, setTbodyContent]
        = useState<ReactNode>(<CartIsEmpty translationsData={translationsData} />);
    const customer = useSelector((state: IState) => state.customer)
    const cartToken = useSelector((state: IState) => state.cartToken)
    const signed = useSelector((state: IState) => state.customer.authenticated);
    const dispatch = useDispatch();
    const hasDownloadableItem = cart.items.some(item => item.type === 'downloadable');
    const schema = schemPageCart(dbName, router.asPath, cart.items, selectedRate?.code);
    const toast = useToast();
    const debounce = useDebounce((quantities: Array<quantitiesType>) => (
        dispatch(cartUpdateQuantities(
            quantities,
            customer,
            cartToken,
            () => toast.open(
                <span className="d-flex chek-fms">
                    <FailSvg/>
                    {translationsData.ProductNotAvailable}
                </span>
            )
        ))
    ), 500);

    const onChangeCallback = (quantity: number, item: ICartItemType) => {
        const updatedContent = quantities.map((elem) => {
            if (elem.itemId === item.id) {
                return {
                    ...elem,
                    value: quantity
                }
            }
            return elem
        })
        setQuantities(updatedContent)
        debounce(updatedContent)
    }

    useEffect(() => {
        if (cart.items.length > 0) {
            setTbodyContent(
                <div className={`cart ${cls.block}`}>
                    <div className={`container ${cls["cart-page-container"]}`}>
                        <table className={cls["cart-table"]}>
                            <TableHead translationsData={translationsData} />
                            <tbody className={cls["cart-table__body"]}>
                                {cart?.items.map((item, index) => {
                                    const maxQty = (() => {
                                        if (item.product.type === "bundle") return item.max_qty + 1
                                        return 50000
                                    })();
                                    return <Fragment key={index}>
                                        <MobileCartItems
                                            item={item}
                                            translationsData={translationsData}
                                            onChangeCallBack={onChangeCallback}
                                            maxQty={maxQty}
                                            quantities={quantities}
                                            subTotal={item.formated_total}
                                            ProductNotAvailableIntl={translationsData?.ProductNotAvailable}
                                        />
                                        <DesktopCartItems
                                            item={item}
                                            DeleteIntl={translationsData?.Delete}
                                            onChangeCallBack={onChangeCallback}
                                            maxQty={maxQty}
                                            quantities={quantities}
                                            subTotal={item.formated_total}
                                            ProductNotAvailableIntl={translationsData?.ProductNotAvailable}
                                        />
                                    </Fragment>
                                })}
                            </tbody>
                        </table>
                        <CartActions
                            translationsData={translationsData}
                            subTotalForCart={cart.sub_total}
                            checkoutHrefHandle={hasDownloadableItem && !signed ? "/signin" : "/checkout"}
                            ToastSignInIntl={translationsData?.ToastSignIn}
                        />
                    </div>
                </div>
            )
        } else {
            setTbodyContent(<CartIsEmpty translationsData={translationsData} />)
        }
    }, [cart.items.length, locale, cart.grand_total]);

    return (
        <>
            <Script
                id={schema.name}
                type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
            />
            <PageHeader dbName={dbName} header="Shopping Cart" breadcrumb={breadcrumb}/>
            <div className={cls["shopping-cart-text"]}>
                <h1>{translationsData.ShoppingCart}</h1>
            </div>
            {tbodyContent}
        </>
    )
}

export default ShopPageCart;
